import lodash from 'lodash';
import { openNativeModal } from 'components/Modal';
import iconAus from 'static/images/currency/AUD.svg';
import iconHk from 'static/images/currency/HKD.svg';
import iconNzl from 'static/images/currency/NZD.svg';
import iconOther from 'static/images/currency/other-bank.svg';
import iconSgp from 'static/images/currency/SGD.svg';
import iconUs from 'static/images/currency/USD.svg';
import achIcon from 'static/images/icons/ach.png';
import checkIcon from 'static/images/icons/check.svg';
import contributionIcon from 'static/images/icons/contribution.svg';
import exchangeIcon from 'static/images/icons/exchange.png';
import iconTransfer from 'static/images/icons/transfer.svg';
import wireIcon from 'static/images/icons/wire.svg';
import IRALimitsModal from './IRALimitsModal';
import { ChoiceBankType, RecommendDepositEnum } from './type';
import './style.scss';

/**
 * 获取入金主方式名称
 * @param type 主方式类型
 * @param isIRA 是否为 IRA 账户
 */
export function getWayName(type: RecommendDepositEnum, isIRA?: boolean) {
    switch (type) {
        case RecommendDepositEnum.Transfer:
            return isIRA ? __('Wire Contribution') : __('转账汇款');
        case RecommendDepositEnum.ACH:
            return isIRA ? __('ACH Contribution') : __('ACH Deposit');
        default:
            return '';
    }
}

/**
 * 获取入金方式icon
 * @param type 入金方式
 * @param isIRA 是否为 IRA 账户
 */
export function getWayIcon(type: string | number, isIRA?: boolean) {
    switch (type) {
        case RecommendDepositEnum.Transfer:
            return isIRA ? wireIcon : exchangeIcon;
        case RecommendDepositEnum.ACH:
            return achIcon;
        case 'contribution':
            return contributionIcon;
        case 'check':
            return checkIcon;
        case 'transfer':
            return iconTransfer;
        default:
            return exchangeIcon;
    }
}

/**
 * 获取入金主方式跳转路径
 * @param type 主方式类型
 * @param isIRA 是否为 IRA 账户
 */
export function getWayPath(type: RecommendDepositEnum, isIRA?: boolean) {
    switch (type) {
        case RecommendDepositEnum.ACH:
            return isIRA ? '/profile/deposit/ach/fill' : '/profile/deposit/ach';
        default:
            return '/profile/deposit';
    }
}

// 转账汇款入金币种列表
export const transferDepositCurrencyList = ['USD', 'HKD', 'SGD', 'NZD', 'AUD', 'EUR', 'GBP', 'CNH'] as const;

export type TransferDepositCurrency = (typeof transferDepositCurrencyList)[number];

function beneficiaryInfoAnalyzer(key) {
    switch (key) {
        case 'payee_region':
            return __('收款人国家/地区');
        case 'payee_city':
            return __('收款人所在城市');
        case 'uen':
            return __('公司注册号 (UEN)');
        case 'payee_address':
            return __('收款人地址');
        case 'SWIFT':
            return __('收款行SWIFT代码');
        case 'payee_bank_full_name':
            return __('AMS-deposit-收款银行');
        case 'payee_bank_region':
            return __('收款银行所在国家/地区');
        case 'payee_bank_city':
            return __('收款银行所在城市');
        case 'payee_bank_addresss':
            return __('收款银行地址');
        case 'agent_bank':
            return __('收款银行代理行');
        case 'agent_bank_SWIFT':
            return __('收款银行代理行SWIFT代码');
        case 'ABA':
            return 'Routing Number (ABA)';
        case 'bsb_number':
            return 'BSB';
        case 'branch_code':
            return __('AMS-deposit-银行代码');
        case 'fps_id':
            return __('FPS 识别码');
        default:
            return key;
    }
}

function parseBeneficiaryInfo(data) {
    data = lodash.pickBy(data, lodash.identity); // 移除对象中的空值

    const indexedProps = [
        'fps_id',
        'uen',
        'payee_region',
        'payee_city',
        'payee_address',
        'payee_bank_full_name',
        'SWIFT',
        'bsb_number',
        'branch_code',
        'ABA',
        'payee_bank_region',
        'payee_bank_city',
        'payee_bank_addresss',
        'agent_bank',
        'agent_bank_SWIFT'
    ];

    return indexedProps
        .concat(lodash.difference(Object.keys(data), indexedProps))
        .map(
            key =>
                key in data && {
                    label: beneficiaryInfoAnalyzer(key),
                    value: data[key],
                    meta: key === 'SWIFT' && __('* 最后三位（XXX）可不填写'),
                    name: key
                }
        )
        .filter(Boolean);
}

// 格式化转账汇款方式的收款信息接口数据
export function parseAccountInfo(data, need_van_account, otherData?: Record<string, any>) {
    if (!data) {
        return {
            need_van_account,
            banklist: [],
            acclist: []
        };
    }

    const { payeeAccountNum, bsbNumber } = otherData || {};
    let accountInfo = [] as any[];
    const payee_account_num = payeeAccountNum || data.payee_account_num;
    const bsb_number = bsbNumber || data.bsb_number;

    try {
        // 添加fps_id  bsb_number 因该变量不在info中且需要与info中的数据进行排序
        data.payee_account_info.fps_id = data.fps_id;
        data.payee_account_info.bsb_number = bsb_number;
        accountInfo = parseBeneficiaryInfo(data.payee_account_info);
    } catch (e) {}

    return {
        need_van_account,
        banklist: data.banklist || [],
        acclist: [
            payee_account_num && {
                label: __('收款账号'),
                value: payee_account_num,
                name: 'payee_account_num'
            },
            data.payee_name && { label: __('收款人姓名'), value: data.payee_name, name: 'payee_name' }
        ]
            .concat(
                data.ps_comment && {
                    label: (
                        <span
                            dangerouslySetInnerHTML={{
                                __html: __("转账附言<span class='text-required'>（必填）</span>")
                            }}
                        />
                    ),
                    value: data.ps_comment,
                    name: 'ps_comment'
                },
                accountInfo
            )
            .filter(Boolean),
        accData: {
            beneficiary_bank: data.payee_account_info.payee_bank_full_name || '',
            beneficiary_bank_account: payee_account_num || '',
            beneficiary_bank_country_code: data.payee_account_info.SWIFT
                ? data.payee_account_info.SWIFT.slice(4, 6)
                : ''
        }
    };
}

// 转账汇款中币种和银行类型的对应
export const currencyBankMap = {
    HKD: 2,
    USD: 3,
    NZD: 4,
    SGD: 6,
    AUD: 7,
    EUR: 5,
    CHN: 1
};

// 根据转账汇款接口信息中的列表key (币种) 获取银行信息
export const getBankName = (id: string | ChoiceBankType) => {
    const key: ChoiceBankType = Number(id);

    switch (key) {
        case ChoiceBankType.Basics:
            return __('其他地区银行账户');
        case ChoiceBankType.HKBank:
            return __('香港银行账户');
        case ChoiceBankType.USBank:
            return __('美国银行账户');
        case ChoiceBankType.NZBank:
            return __('新西兰银行账户');
        case ChoiceBankType.SGBank:
            return __('新加坡银行账户');
        case ChoiceBankType.AUBank:
            return __('澳大利亚银行账户');
        case ChoiceBankType.Other:
        default:
            return __('海外银行账户');
    }
};

// 获取银行对应 logo
export const getBankIcon = id => {
    switch (String(id)) {
        case '2':
            return iconHk;
        case '3':
            return iconUs;
        case '4':
            return iconNzl;
        case '6':
            return iconSgp;
        case '7':
            return iconAus;
        case '5':
        default:
            return iconOther;
    }
};

export function openIRALimitsModal() {
    return openNativeModal({
        maskClosable: false,
        closable: true,
        width: '100%',
        className: 'deposit-ira-limits-modal',
        component: props => <IRALimitsModal {...props} />
    });
}
